import { useEffect, useState } from "react";
import { Link } from "wouter";
import { ArrowRight, BookOpen, CalendarDays, ChevronLeft, ChevronRight, Clock, Sparkles } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import type { BlogPost } from "@/data/blogPosts";
import { trackRelatedPostClick } from "@/lib/analytics";
import { ResponsiveImage } from "@/components/ResponsiveImage";

import SchemaScript from "@/components/SchemaScript";
interface RelatedAIInsightsCarouselProps {
  posts: BlogPost[];
  currentSlug: string;
  heading?: string;
  description?: string;
  /** Optional editorial rationale keyed by related post slug. */
  relevanceSummaries?: Record<string, string>;
  itemListName?: string;
  cardLabel?: string;
  previousLabel?: string;
  nextLabel?: string;
}

function readingTime(content: string) {
  return Math.max(1, Math.ceil(content.split(/\s+/).length / 200));
}

function formatDate(date: string) {
  return new Date(date).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function trackInsightClick(fromSlug: string, toSlug: string, position: number) {
  trackRelatedPostClick({
    from_slug: fromSlug,
    to_slug: toSlug,
    position,
    placement: "related_ai_insights_carousel",
  });
}

function InsightCard({
  post,
  index,
  currentSlug,
  cardLabel,
  relevanceSummary,
}: {
  post: BlogPost;
  index: number;
  currentSlug: string;
  cardLabel: string;
  relevanceSummary?: string;
}) {
  return (
    <Link href={`/blog/${post.slug}/`}>
      <article
        onClick={() => trackInsightClick(currentSlug, post.slug, index + 1)}
        className="group flex min-h-[440px] h-full cursor-pointer flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.055] transition-colors hover:border-amber-400/45 hover:bg-white/[0.08]"
        aria-label={`Read related AI insight: ${post.title}`}
      >
        <div className="relative h-44 overflow-hidden bg-slate-800">
          <ResponsiveImage
            src={post.heroImage}
            alt={post.heroAlt ?? post.title}
            width={640}
            height={360}
            sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, calc(100vw - 32px)"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.035]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent" />
          <span className="absolute left-3 top-3 rounded-full bg-amber-400 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-950">
            {cardLabel} {index + 1}
          </span>
        </div>

        <div className="flex flex-1 flex-col p-5">
          <div className="mb-3 flex flex-wrap gap-2">
            {post.tags.slice(0, 2).map((tag) => (
              <span key={tag} className="text-[10px] font-semibold uppercase tracking-wider text-amber-300/90">
                {tag}
              </span>
            ))}
          </div>
          <h3 className="mb-3 line-clamp-3 font-serif text-xl leading-snug text-white transition-colors group-hover:text-amber-300">
            {post.title}
          </h3>
          <p className="mb-5 line-clamp-3 text-sm leading-relaxed text-white/55">
            {post.excerpt}
          </p>
          {relevanceSummary && (
            <p className="mb-5 border-l-2 border-amber-400/65 pl-3 text-xs leading-relaxed text-amber-100/75 line-clamp-3">
              <span className="font-semibold text-amber-300">Why it matters: </span>
              {relevanceSummary}
            </p>
          )}
          <div className="mt-auto flex items-center justify-between gap-4 border-t border-white/10 pt-4">
            <div className="flex flex-wrap items-center gap-3 text-[11px] text-white/45">
              <span className="flex items-center gap-1">
                <CalendarDays className="h-3 w-3" />
                {formatDate(post.date)}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {readingTime(post.content)} min
              </span>
            </div>
            <ArrowRight className="h-4 w-4 shrink-0 text-amber-400 transition-transform group-hover:translate-x-1" />
          </div>
        </div>
      </article>
    </Link>
  );
}

export default function RelatedAIInsightsCarousel({
  posts,
  currentSlug,
  heading = "Related AI Insights",
  description = "Older analysis selected by topic, so you can follow the evidence behind this story.",
  relevanceSummaries = {},
  itemListName = heading,
  cardLabel = "Insight",
  previousLabel = "Show previous AI insights",
  nextLabel = "Show next AI insights",
}: RelatedAIInsightsCarouselProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [slideCount, setSlideCount] = useState(0);

  useEffect(() => {
    if (!api) return;

    const updateState = () => {
      setSelectedIndex(api.selectedScrollSnap());
      setSlideCount(api.scrollSnapList().length);
    };

    updateState();
    api.on("select", updateState);
    api.on("reInit", updateState);

    return () => {
      api.off("select", updateState);
      api.off("reInit", updateState);
    };
  }, [api]);

  if (!posts.length) return null;

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: itemListName,
    itemListElement: posts.map((post, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: `https://integrated.social/blog/${post.slug}/`,
      name: post.title,
    })),
  };

  return (
    <section
      aria-labelledby="related-ai-insights-heading"
      className="mt-0 -mx-4 rounded-b-2xl bg-slate-900 px-4 pt-10 pb-14 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 xl:-mx-16 xl:px-16"
    >
      <SchemaScript schema={JSON.stringify(itemListSchema)} />

      <div className="mb-7 flex items-end justify-between gap-4">
        <div className="max-w-2xl">
          <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-amber-400">
            <Sparkles className="h-3.5 w-3.5" />
            Continue the research
          </p>
          <h2 id="related-ai-insights-heading" className="font-serif text-2xl text-white md:text-3xl">
            {heading}
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-white/55">
            {description}
          </p>
        </div>

        <div className="hidden items-center gap-2 sm:flex lg:hidden" aria-label="Carousel controls">
          <button
            type="button"
            onClick={() => api?.scrollPrev()}
            disabled={!api?.canScrollPrev()}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white transition-colors hover:border-amber-400/50 hover:text-amber-300 disabled:cursor-not-allowed disabled:opacity-35"
            aria-label={previousLabel}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => api?.scrollNext()}
            disabled={!api?.canScrollNext()}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white transition-colors hover:border-amber-400/50 hover:text-amber-300 disabled:cursor-not-allowed disabled:opacity-35"
            aria-label={nextLabel}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div data-testid="related-ai-insights-grid" className="hidden lg:grid lg:grid-cols-3 gap-5">
        {posts.map((post, index) => (
          <InsightCard
            key={post.slug}
            post={post}
            index={index}
            currentSlug={currentSlug}
            cardLabel={cardLabel}
            relevanceSummary={relevanceSummaries[post.slug]}
          />
        ))}
      </div>

      <Carousel
        setApi={setApi}
        opts={{ align: "start", containScroll: "trimSnaps" }}
        aria-label={heading}
        className="lg:hidden"
      >
        <CarouselContent className="-ml-3 md:-ml-4">
          {posts.map((post, index) => (
            <CarouselItem
              key={post.slug}
              className="basis-[88%] pl-3 sm:basis-[58%] md:pl-4 lg:basis-[38%] xl:basis-[34%]"
            >
              <InsightCard
                post={post}
                index={index}
                currentSlug={currentSlug}
                cardLabel={cardLabel}
                relevanceSummary={relevanceSummaries[post.slug]}
              />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      <div className="mt-6 flex items-center justify-between gap-4 lg:hidden">
        <p className="text-xs text-white/45" aria-live="polite">
          Insight {Math.min(selectedIndex + 1, slideCount || 1)} of {slideCount || 1}
        </p>
        <div className="flex items-center gap-1.5" aria-label="Carousel position">
          {Array.from({ length: slideCount }).map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => api?.scrollTo(index)}
              className={`h-2 rounded-full transition-all ${
                selectedIndex === index ? "w-7 bg-amber-400" : "w-2 bg-white/25 hover:bg-white/45"
              }`}
              aria-label={`Go to insight ${index + 1}`}
              aria-current={selectedIndex === index ? "true" : undefined}
            />
          ))}
        </div>
        <Link href="/blog/" className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-400 hover:text-amber-300">
          <BookOpen className="h-3.5 w-3.5" />
          All insights
        </Link>
      </div>
    </section>
  );
}
